/**
 * NOTE ON NAMING: This component is called "LearnPanel" in code but displays as
 * "Research" in the UI. The internal "Learn" naming is historical - the feature
 * was renamed to "Research" to better describe its purpose (craft learning and
 * research assistance). All Learn* components, services, and database tables
 * implement the "Research Panel" feature.
 */

import React, { useState, useEffect, useCallback } from 'react';
import LearnChatTab from './LearnChatTab';
import LearnLibraryTab from './LearnLibraryTab';
import { learnApi } from '../../api/learn';
import { TextSizeStepper } from '../Common/TextSizeStepper';
import { usePanelZoom, PANEL_ZOOM } from '../../hooks/usePanelZoom';
import { CLOSE_RESEARCH } from '../../utils/writerCopy';
import './LearnPanel.css';

const DEFAULT_WIDTH = 400;
const MIN_WIDTH = 360;
const MAX_WIDTH = 550;

/**
 * LearnPanel - Side panel for writing craft learning and research (UI: "Research Panel").
 *
 * Features:
 * - Chat tab for conversations with AI about writing craft
 * - Library tab for organizing bookmarked insights
 * - Isolated from story context (no semantic search, Bible, etc.)
 * - Project-scoped conversations, user-scoped library
 */
export default function LearnPanel({
  projectId,
  onClose,
  spawnContext,
  width,
  onWidthChange
}) {
  const [activeTab, setActiveTab] = useState('chat');
  const [conversationId, setConversationId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isResizing, setIsResizing] = useState(false);
  const [panelZoom, setPanelZoom] = usePanelZoom();

  // Load or create conversation on mount/projectId change
  useEffect(() => {
    if (!projectId) return;

    const initConversation = async () => {
      try {
        setIsLoading(true);
        setError(null);

        const response = await learnApi.getOrCreateConversation(projectId, spawnContext);

        if (response?.data) {
          setConversationId(response.data.conversation_id);
          setMessages(response.data.messages || []);
        }
      } catch (err) {
        console.error('Failed to initialize Learn conversation:', err);
        // Handle 404 specifically - project may have been deleted or user doesn't own it
        if (err.response?.status === 404 || err.message?.includes('404')) {
          setError('Project not found. Please select a different project.');
        } else {
          setError('Failed to load conversation. Please try again.');
        }
      } finally {
        setIsLoading(false);
      }
    };

    initConversation();
  }, [projectId, spawnContext]);

  // Handle new message sent
  const handleMessageSent = useCallback((userMessage, assistantMessage) => {
    setMessages(prev => [...prev, userMessage, assistantMessage]);
  }, []);

  // Handle bookmark created - update message's bookmark_id
  const handleBookmarkCreated = useCallback((messageId, bookmarkId) => {
    setMessages(prev => prev.map(msg =>
      msg.id === messageId ? { ...msg, bookmark_id: bookmarkId } : msg
    ));
  }, []);

  // Resize handlers
  const handleResizeStart = useCallback((e) => {
    e.preventDefault();
    setIsResizing(true);
  }, []);

  useEffect(() => {
    if (!isResizing) return;

    const handleMouseMove = (e) => {
      const newWidth = window.innerWidth - e.clientX;
      const clamped = Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, newWidth));
      onWidthChange?.(clamped);
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isResizing, onWidthChange]);

  return (
    <div className="learn-panel" style={{ width: width || DEFAULT_WIDTH }}>
      {/* Resize handle */}
      <div
        className={`learn-panel__resize-handle ${isResizing ? 'learn-panel__resize-handle--active' : ''}`}
        onMouseDown={handleResizeStart}
      />

      {/* Header with tabs */}
      <div className="learn-panel__header">
        <div className="learn-panel__tabs">
          <button
            className={`learn-panel__tab ${activeTab === 'chat' ? 'learn-panel__tab--active' : ''}`}
            onClick={() => setActiveTab('chat')}
          >
            Chat
          </button>
          <button
            className={`learn-panel__tab ${activeTab === 'library' ? 'learn-panel__tab--active' : ''}`}
            onClick={() => setActiveTab('library')}
          >
            Library
          </button>
        </div>
        <TextSizeStepper
          value={panelZoom}
          onChange={setPanelZoom}
          min={PANEL_ZOOM.min}
          max={PANEL_ZOOM.max}
          step={PANEL_ZOOM.step}
          label="Panel text size"
        />
        <button
          type="button"
          className="learn-panel__close-btn"
          onClick={onClose}
          aria-label={CLOSE_RESEARCH}
          title={CLOSE_RESEARCH}
        >
          ✕
        </button>
      </div>

      {/* Content (Item 2: shared panel zoom) */}
      <div className="learn-panel__content" style={{ zoom: panelZoom / 100 }}>
        {isLoading ? (
          <div className="learn-panel__loading">
            <div className="learn-panel__spinner" />
            <span>Loading...</span>
          </div>
        ) : error ? (
          <div className="learn-panel__error">
            <p>{error}</p>
            <div className="learn-panel__error-actions">
              <button onClick={() => window.location.reload()}>Retry</button>
              <button onClick={onClose} className="learn-panel__error-close">Close Panel</button>
            </div>
          </div>
        ) : activeTab === 'chat' ? (
          <LearnChatTab
            conversationId={conversationId}
            messages={messages}
            onMessageSent={handleMessageSent}
            onBookmarkCreated={handleBookmarkCreated}
          />
        ) : (
          <LearnLibraryTab />
        )}
      </div>
    </div>
  );
}
